// src/pages/Admin/DepensesRecettes/EditDepenseRecetteModal.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes, FaUpload, FaImage } from "react-icons/fa";
import { PiFilePdfFill } from "react-icons/pi";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import {
  updateDepenseRecetteClub,
  getDepensesRecettesClub,
} from "../../../services/depensesrecettesclubServices";
import { getSettings } from "../../../services/settingsServices";
import { getAllPlanComptables } from "../../../services/plancomptableServices"; // 👈 NEW

// Services upload local (image/PDF) pour dépense/recette
import {
  uploadDepensesRecettesPhoto,
  toAbsoluteDepensesRecettesPhotoUrl,
} from "../../../services/depensesrecettesclubPhotoServices";

const REGLEMENTS = [
  "Virement",
  "Carte bancaire",
  "Espèces",
  "Chèque",
  "Prélèvement",
  "Autre…",
];

const DEFAULT_PHOTO = "/img/no-image.jpg";

const todayStr = () => new Date().toISOString().slice(0, 10);

// Helper cache-busting
const withBust = (url, v) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}v=${v}` : url;

function neighborsFromSeason(season) {
  const m = /^(\d{4})\s*-\s*(\d{4})$/.exec(season || "");
  if (!m)
    return { prev: season || "", current: season || "", next: season || "" };
  const y1 = parseInt(m[1], 10);
  const y2 = parseInt(m[2], 10);
  return {
    prev: `${y1 - 1}-${y2 - 1}`,
    current: `${y1}-${y2}`,
    next: `${y1 + 1}-${y2 + 1}`,
  };
}

const isPdfHref = (href = "") => /\.pdf(\?|$)/i.test(href.trim());

export default function EditDepenseRecetteModal({
  isOpen,
  onClose,
  refreshList,
  depenseRecette,
}) {
  const [formData, setFormData] = useState({
    saison: "",
    date_operation: "",
    designation: "",
    categorie: "",
    beneficiaire: "",
    montant: "",
    type_reglement: "",
    type_operation: "Dépense",
    photo_url: "",
    lien_url: "",
    plan_comptable_id: "", // 👈 NEW
  });

  // Contexte
  const [loading, setLoading] = useState(false);
  const [seasonOptions, setSeasonOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [planComptables, setPlanComptables] = useState([]); // 👈 NEW

  // États “autre / nouvelle valeur”
  const [useNewCategorie, setUseNewCategorie] = useState(false);
  const [newCategorie, setNewCategorie] = useState("");
  const [useOtherReglement, setUseOtherReglement] = useState(false);
  const [otherReglement, setOtherReglement] = useState("");

  // Aperçu image/PDF
  const [imgOk, setImgOk] = useState(true);
  const [imgVersion, setImgVersion] = useState(0);

  // Enregistrement
  const [submitting, setSubmitting] = useState(false);

  // Sous-modale upload
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp"); // pour images

  // Refs (focus/submit)
  const dialogRef = useRef(null);
  const formRef = useRef(null);

  const preventEnterSubmit = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Initial dérivé de l’item courant
  const initial = useMemo(() => {
    const d = depenseRecette?.date_operation || "";
    const dateOnly = d.length >= 10 ? d.slice(0, 10) : todayStr();
    return {
      saison: depenseRecette?.saison || "",
      date_operation: dateOnly,
      designation: depenseRecette?.designation || "",
      categorie: depenseRecette?.categorie || "",
      beneficiaire: depenseRecette?.beneficiaire || "",
      montant:
        depenseRecette?.montant === null ||
        depenseRecette?.montant === undefined
          ? ""
          : String(depenseRecette?.montant),
      type_reglement: depenseRecette?.type_reglement || "",
      type_operation: depenseRecette?.type_operation || "Dépense",
      photo_url:
        depenseRecette?.photo_url && depenseRecette.photo_url.trim().length > 0
          ? depenseRecette.photo_url
          : "",
      lien_url: depenseRecette?.lien_url || "",
      plan_comptable_id:
        depenseRecette?.plan_comptable_id === null ||
        depenseRecette?.plan_comptable_id === undefined
          ? ""
          : String(depenseRecette?.plan_comptable_id), // stocké en string pour le select
    };
  }, [depenseRecette]);

  // Charge contextes (saison/catégories/plan comptable)
  const loadContext = useCallback(async (recordSeason) => {
    setLoading(true);
    try {
      const settings = await getSettings();
      const saisonActive = settings?.[0]?.saison_active || recordSeason || "";
      const aroundActive = neighborsFromSeason(saisonActive);
      const aroundRecord = neighborsFromSeason(recordSeason || saisonActive);
      const seasons = Array.from(
        new Set(
          [
            aroundActive.prev,
            aroundActive.current,
            aroundActive.next,
            aroundRecord.prev,
            aroundRecord.current,
            aroundRecord.next,
          ].filter(Boolean)
        )
      );
      setSeasonOptions(seasons);

      const ops = await getDepensesRecettesClub();
      const cats = Array.from(
        new Set(
          (Array.isArray(ops) ? ops : [])
            .map((o) => o.categorie)
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, "fr"));
      setCategories(cats);

      // 👇 NEW: charger le plan comptable
      const plans = await getAllPlanComptables();
      const sortedPlans = (Array.isArray(plans) ? plans : []).sort((a, b) => {
        if (a.sens === b.sens) return a.ordre - b.ordre;
        return a.sens === "RECETTE" ? -1 : 1;
      });
      setPlanComptables(sortedPlans);
    } catch (e) {
      console.error(e);
      toast.error(
        "Impossible de charger la configuration (saison/catégories/plan comptable)."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Hydrate à l’ouverture
  useEffect(() => {
    if (!isOpen || !depenseRecette) return;

    setFormData(initial);
    setImgVersion((v) => v + 1);

    setUseNewCategorie(false);
    setNewCategorie("");
    setUseOtherReglement(false);
    setOtherReglement("");
    setImgOk(true);

    loadContext(depenseRecette.saison || "");
  }, [isOpen, depenseRecette, initial, loadContext]);

  // Verrouille le scroll + ESC. Ferme d’abord la sous-modale, puis le parent.
  useEffect(() => {
    if (!isOpen) {
      setIsUploadOpen(false);
      setSelectedFile(null);
      setUploading(false);
      setUploadProgress(0);
      return;
    }
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      if (isUploadOpen) {
        setIsUploadOpen(false);
        return;
      }
      handleClose();
    };
    window.addEventListener("keydown", onEsc);

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='designation']")?.focus();
    }, 0);

    return () => {
      root.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onEsc);
      clearTimeout(t);
    };
  }, [isOpen, isUploadOpen]);

  // Ajuste “nouvelle cat / autre règlement” si valeur hors liste
  useEffect(() => {
    if (!isOpen) return;
    if (
      formData.categorie &&
      categories.length > 0 &&
      !categories.includes(formData.categorie)
    ) {
      setUseNewCategorie(true);
      setNewCategorie(formData.categorie);
    }
    if (
      formData.type_reglement &&
      !REGLEMENTS.includes(formData.type_reglement)
    ) {
      setUseOtherReglement(true);
      setOtherReglement(formData.type_reglement);
    }
  }, [isOpen, categories, formData.categorie, formData.type_reglement]);

  // Dirty check simple
  const isDirty =
    formData.saison !== initial.saison ||
    formData.date_operation !== initial.date_operation ||
    (formData.designation || "") !== (initial.designation || "") ||
    (formData.categorie || "") !== (initial.categorie || "") ||
    (formData.beneficiaire || "") !== (initial.beneficiaire || "") ||
    String(formData.montant) !== String(initial.montant) ||
    (formData.type_reglement || "") !== (initial.type_reglement || "") ||
    (formData.type_operation || "") !== (initial.type_operation || "") ||
    (formData.photo_url || "") !== (initial.photo_url || "") ||
    (formData.lien_url || "") !== (initial.lien_url || "") ||
    String(formData.plan_comptable_id || "") !==
      String(initial.plan_comptable_id || ""); // 👈 NEW

  const handleClose = () => {
    if (uploading || submitting) return;
    if (
      !isDirty ||
      window.confirm("Des modifications ne sont pas enregistrées. Fermer ?")
    ) {
      onClose?.();
    }
  };

  const onBackdrop = (e) => {
    if (e.target !== e.currentTarget) return;
    if (isUploadOpen) return;
    handleClose();
  };

  const canSubmit = useMemo(() => {
    const hasSeason = !!formData.saison;
    const hasDate = !!formData.date_operation;
    const hasDesignation = !!formData.designation?.trim();
    const hasCategorie = useNewCategorie
      ? !!newCategorie?.trim()
      : !!formData.categorie;
    const hasMontant =
      formData.montant !== "" && !Number.isNaN(Number(formData.montant));
    const hasReglement = useOtherReglement
      ? !!otherReglement?.trim()
      : !!formData.type_reglement;
    // plan_comptable_id non obligatoire
    return (
      !loading &&
      !submitting &&
      hasSeason &&
      hasDate &&
      hasDesignation &&
      hasCategorie &&
      hasMontant &&
      hasReglement
    );
  }, [
    formData,
    newCategorie,
    otherReglement,
    useNewCategorie,
    useOtherReglement,
    loading,
    submitting,
  ]);

  // Champs
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "categorie") {
      if (value === "__new__") {
        setUseNewCategorie(true);
        setFormData((p) => ({ ...p, categorie: "" }));
      } else {
        setUseNewCategorie(false);
        setNewCategorie("");
        setFormData((p) => ({ ...p, categorie: value }));
      }
      return;
    }

    if (name === "type_reglement") {
      if (value === "__other__") {
        setUseOtherReglement(true);
        setFormData((p) => ({ ...p, type_reglement: "" }));
      } else {
        setUseOtherReglement(false);
        setOtherReglement("");
        setFormData((p) => ({ ...p, type_reglement: value }));
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        categorie: useNewCategorie ? newCategorie.trim() : formData.categorie,
        type_reglement: useOtherReglement
          ? otherReglement.trim()
          : formData.type_reglement,
        montant: Number(formData.montant),
        // 👇 normaliser l'id: chaîne vide => null, sinon nombre
        plan_comptable_id: formData.plan_comptable_id
          ? Number(formData.plan_comptable_id)
          : null,
      };
      await updateDepenseRecetteClub(depenseRecette.id, payload);
      toast.success("Dépense / recette mise à jour avec succès");
      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la mise à jour", error);
      toast.error("Erreur lors de la mise à jour de l'opération");
    } finally {
      setSubmitting(false);
    }
  };

  // Upload justificatif (image/PDF) — sous-modale
  const onFileChange = (e) => setSelectedFile(e.target.files?.[0] || null);

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!depenseRecette?.id) {
      toast.error("Ligne introuvable — impossible d’associer le justificatif.");
      return;
    }
    if (!selectedFile) {
      toast.warn("Sélectionnez un fichier image ou PDF.");
      return;
    }

    // Validation front
    const okType =
      /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type) ||
      selectedFile.type === "application/pdf";
    if (!okType) {
      toast.error("Format non supporté. Utilisez PNG / JPG / WEBP / PDF.");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const params =
        selectedFile.type === "application/pdf" ? {} : { format: outputFormat };

      const updated = await uploadDepensesRecettesPhoto(
        depenseRecette.id,
        selectedFile,
        {
          ...params,
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const p = Math.round((evt.loaded / evt.total) * 100);
            setUploadProgress(p);
          },
        }
      );

      setFormData((prev) => ({
        ...prev,
        photo_url: updated?.photo_url || prev.photo_url,
      }));
      setImgVersion((v) => v + 1);

      toast.success("Justificatif téléversé.");
      setIsUploadOpen(false);
      setSelectedFile(null);
    } catch (err) {
      console.error("Upload justificatif dépense/recette:", err);
      toast.error("Erreur lors du téléversement.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Toujours déclarer les hooks AVANT tout early return
  const sensWanted = useMemo(
    () => (formData.type_operation === "Recette" ? "RECETTE" : "DEPENSE"),
    [formData.type_operation]
  );

  const filteredPlans = useMemo(
    () => planComptables.filter((p) => p.sens === sensWanted),
    [planComptables, sensWanted]
  );

  if (!isOpen || !depenseRecette) return null;

  // Prépare l’aperçu (image/PDF). Utilise toAbsolute* pour /uploads/…
  const previewAbs = toAbsoluteDepensesRecettesPhotoUrl(
    formData.photo_url?.trim() ? formData.photo_url : DEFAULT_PHOTO
  );
  const preview = withBust(previewAbs, imgVersion);
  const isPdf = isPdfHref(formData.photo_url || "");

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={onBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Modifier une dépense/recette"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-3xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/60 dark:ring-slate-800/60 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
              <FaImage />
            </span>
            <h3 className="text-white text-lg sm:text-xl font-bold uppercase">
              Modifier la dépense / recette
            </h3>
          </div>
          <button
            type="button"
            aria-label="Fermer"
            className="h-11 w-11 grid place-items-center rounded-xl bg-white/15 hover:bg-white/25 text-white"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClose();
            }}
          >
            <FaTimes className="text-[18px]" />
          </button>
        </div>

        {/* ======= FORMULAIRE (corps + footer) ======= */}
        <form
          ref={formRef}
          id="edit-depense-recette-form"
          onSubmit={handleSubmit}
          className="flex-1 min-h-0 flex flex-col"
        >
          {/* Corps scrollable */}
          <div
            className="flex-1 min-h-0 overflow-y-auto p-5 sm:p-6 space-y-5"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-5">
              {/* Colonne justificatif */}
              <div className="flex flex-col gap-3">
                <div className="relative h-36 w-full rounded-xl bg-white/70 dark:bg-slate-900/60 ring-1 ring-slate-200 dark:ring-slate-800 overflow-hidden flex items-center justify-center">
                  {isPdf ? (
                    <a
                      href={toAbsoluteDepensesRecettesPhotoUrl(
                        formData.photo_url
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-slate-800 dark:text-slate-100"
                      title="Ouvrir le PDF"
                    >
                      <PiFilePdfFill className="text-3xl" />
                      <span className="text-sm">Ouvrir le PDF</span>
                    </a>
                  ) : (
                    <img
                      src={preview}
                      alt={formData.designation || "Justificatif"}
                      onError={(e) => {
                        e.currentTarget.src = DEFAULT_PHOTO;
                        setImgOk(false);
                      }}
                      className="h-full w-full object-contain"
                    />
                  )}
                  <span className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full grid place-items-center bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 shadow ring-2 ring-white dark:ring-slate-900">
                    {isPdf ? (
                      <PiFilePdfFill className="text-xs" />
                    ) : (
                      <FaImage className="text-xs" />
                    )}
                  </span>
                </div>

                <Button
                  type="button"
                  text="Téléverser un justificatif"
                  icon={FaUpload}
                  className="!rounded-xl gap-2"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsUploadOpen(true);
                  }}
                />

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Lien de l'image
                  </label>
                  <input
                    type="text"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={(e) => {
                      setImgOk(true);
                      handleChange(e);
                      setImgVersion((v) => v + 1);
                    }}
                    placeholder="https://… ou /uploads/…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Lien externe (facture, justificatif, etc.) — optionnel
                  </label>
                  <input
                    type="url"
                    name="lien_url"
                    value={formData.lien_url}
                    onChange={handleChange}
                    placeholder="https://…"
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
              </div>

              {/* Colonne formulaire */}
              <div className="grid grid-cols-1 gap-4">
                {/* Saison + Date */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                      Saison
                    </label>
                    <select
                      name="saison"
                      value={formData.saison}
                      onChange={handleChange}
                      disabled={loading}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      required
                    >
                      <option value="">
                        {loading ? "Chargement…" : "— Sélectionner —"}
                      </option>
                      {seasonOptions.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                      Date de l’opération
                    </label>
                    <input
                      type="date"
                      name="date_operation"
                      value={formData.date_operation}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      required
                    />
                  </div>
                </div>

                {/* Désignation + Bénéficiaire */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                      Désignation
                    </label>
                    <input
                      type="text"
                      name="designation"
                      placeholder="Ex : Achat maillots, Subvention mairie…"
                      value={formData.designation}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                      Bénéficiaire
                    </label>
                    <input
                      type="text"
                      name="beneficiaire"
                      placeholder="Fournisseur / Organisme / Personne"
                      value={formData.beneficiaire}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      required
                    />
                  </div>
                </div>

                {/* Catégorie + Type d’opération */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                      Catégorie
                    </label>
                    <select
                      name="categorie"
                      value={useNewCategorie ? "__new__" : formData.categorie}
                      onChange={handleChange}
                      disabled={loading}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      required={!useNewCategorie}
                    >
                      <option value="">
                        {loading ? "Chargement…" : "— Sélectionner —"}
                      </option>
                      {categories.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                      <option value="__new__">— Nouvelle catégorie…</option>
                    </select>

                    {useNewCategorie && (
                      <input
                        type="text"
                        placeholder="Saisir une nouvelle catégorie"
                        value={newCategorie}
                        onChange={(e) => setNewCategorie(e.target.value)}
                        onKeyDown={preventEnterSubmit}
                        autoFocus
                        className="mt-2 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                        required
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                      Type d’opération
                    </label>
                    <select
                      name="type_operation"
                      value={formData.type_operation}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      required
                    >
                      <option value="Dépense">Dépense</option>
                      <option value="Recette">Recette</option>
                    </select>
                  </div>
                </div>

                {/* 👇 Compte comptable */}
                <div>
                  <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                    Compte comptable (Plan)
                  </label>
                  <select
                    name="plan_comptable_id"
                    value={formData.plan_comptable_id}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    title={
                      formData.plan_comptable_id
                        ? (() => {
                            const p = planComptables.find(
                              (x) =>
                                String(x.id) ===
                                String(formData.plan_comptable_id)
                            );
                            return p ? `${p.code} — ${p.intitule}` : "";
                          })()
                        : "Non affecté"
                    }
                  >
                    <option value="">— Non affecté —</option>
                    {filteredPlans.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.code} — {p.intitule}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Astuce&nbsp;: la liste est filtrée selon le type d’opération
                    (Recette/Dépense).
                  </p>
                </div>

                {/* Montant + Type de règlement */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                      Montant (€)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="montant"
                      placeholder="0,00"
                      value={formData.montant}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                      Type de règlement
                    </label>
                    <select
                      name="type_reglement"
                      value={
                        useOtherReglement
                          ? "__other__"
                          : formData.type_reglement
                      }
                      onChange={handleChange}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      required={!useOtherReglement}
                    >
                      <option value="">— Sélectionner —</option>
                      {REGLEMENTS.map((r) => (
                        <option
                          key={r}
                          value={r === "Autre…" ? "__other__" : r}
                        >
                          {r}
                        </option>
                      ))}
                    </select>

                    {useOtherReglement && (
                      <input
                        type="text"
                        placeholder="Précisez le type de règlement"
                        value={otherReglement}
                        onChange={(e) => setOtherReglement(e.target.value)}
                        onKeyDown={preventEnterSubmit}
                        autoFocus
                        className="mt-2 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                        required
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="h-2" />
          </div>

          {/* Footer sticky — DANS le form */}
          <div className="sticky bottom-0 z-10 px-5 sm:px-6 py-3 border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95">
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                text="Annuler"
                icon={FaTimes}
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClose();
                }}
              />
              <Button
                type="button"
                text={submitting ? "Enregistrement…" : "Enregistrer"}
                icon={FaSave}
                className="!rounded-xl gap-2"
                disabled={!canSubmit}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  formRef.current?.requestSubmit();
                }}
              />
              <button
                type="submit"
                className="sr-only"
                aria-hidden="true"
                tabIndex={-1}
              >
                submit
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Sous-modale upload justificatif (image/PDF) */}
      {isUploadOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4"
            onClick={(e) =>
              e.currentTarget === e.target && setIsUploadOpen(false)
            }
            role="dialog"
            aria-modal="true"
            aria-label="Téléverser un justificatif"
          >
            <div
              className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200 dark:ring-slate-800 p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 className="text-slate-900 dark:text-white font-semibold mb-3">
                Téléverser un justificatif (image ≤ 1200px ou PDF)
              </h4>

              <form onSubmit={handleUpload} className="space-y-4">
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={onFileChange}
                  className="block w-full text-sm text-slate-700 dark:text-slate-200"
                />

                {/* Choix format réservé aux images */}
                {selectedFile && selectedFile.type !== "application/pdf" && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Format</span>
                    <select
                      className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-2 py-1 text-sm"
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value)}
                      disabled={uploading}
                    >
                      <option value="webp">WEBP (recommandé)</option>
                      <option value="jpg">JPG</option>
                      <option value="png">PNG</option>
                    </select>
                  </div>
                )}

                {uploading && (
                  <div>
                    <progress
                      className="progress w-full"
                      value={uploadProgress}
                      max="100"
                    />
                    <div className="text-xs mt-1">{uploadProgress}%</div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    text="Fermer"
                    icon={FaTimes}
                    className="!rounded-xl"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsUploadOpen(false);
                    }}
                  />
                  <Button
                    type="submit"
                    text={uploading ? "Transfert…" : "Sauvegarder"}
                    icon={FaSave}
                    className="!rounded-xl"
                    disabled={uploading || !selectedFile}
                  />
                </div>
              </form>

              <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
                Les images sont redimensionnées côté serveur (largeur max
                1200&nbsp;px) et converties au format choisi. Les PDF sont
                stockés tels quels.
              </p>
            </div>
          </div>,
          document.body
        )}
    </div>,
    document.body
  );
}
